import { useEffect, useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import {
  ApiError,
  dns as dnsApi,
  domains as domainsApi,
  type CloudflareIntegration,
  type DomainWithDns,
  type NsCheckResult,
} from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { CloudflareConnect } from '@/components/CloudflareConnect';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Button, ButtonLink, StatusPill as UIStatusPill, Surface } from '@/components/ui';

export default function AddDomain() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const existingId = params.get('id');

  const [hostname, setHostname] = useState('');
  const [domain, setDomain] = useState<DomainWithDns | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cfIntegration, setCfIntegration] = useState<CloudflareIntegration | null>(null);
  const [autoAddBusy, setAutoAddBusy] = useState(false);
  const [autoAddResult, setAutoAddResult] = useState<string | null>(null);
  const [nsCheck, setNsCheck] = useState<NsCheckResult | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);

  // If we got ?id=, load the existing domain so user can re-see DNS instructions.
  useEffect(() => {
    if (!existingId) return;
    domainsApi
      .get(existingId)
      .then(setDomain)
      .catch((e) => setErr(e.message || 'Failed to load domain.'));
  }, [existingId]);

  // Poll for status updates while not fully active. With wildcard enabled,
  // "fully active" requires BOTH the apex and the wildcard hostnames to
  // verify — otherwise subdomain links will 502 until SSL provisions.
  useEffect(() => {
    if (!domain) return;
    const apexReady = domain.verificationStatus === 'active' && domain.sslStatus === 'active';
    const wildcardReady = domain.wildcardEnabled
      ? domain.wildcardVerificationStatus === 'active' && domain.wildcardSslStatus === 'active'
      : true;
    if (apexReady && wildcardReady) return;
    const t = setInterval(() => {
      domainsApi.get(domain.id).then(setDomain).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [domain]);

  // Detect whether the domain uses Cloudflare DNS — runs once per domain
  // so we only surface the CF integration card when it's actually relevant.
  useEffect(() => {
    if (!domain) {
      setNsCheck(null);
      return;
    }
    dnsApi
      .checkNs(domain.hostname)
      .then(setNsCheck)
      .catch(() => setNsCheck(null));
  }, [domain?.hostname]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const result = await domainsApi.create(hostname.trim().toLowerCase());
      setDomain(result);
      navigate(`/app/domains/add?id=${result.id}`, { replace: true });
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? errorMessageFor(e.code, e.detail)
          : 'Something went wrong.';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  async function onAutoAddDns() {
    if (!domain) return;
    setAutoAddBusy(true);
    setAutoAddResult(null);
    setErr(null);
    try {
      const res = await domainsApi.autoAddDns(domain.id);
      const created = res.records.filter((r) => r.status === 'created').length;
      const updated = res.records.filter((r) => r.status === 'updated').length;
      const present = res.records.filter((r) => r.status === 'already_present').length;
      const errored = res.records.filter((r) => r.status === 'error');
      const summary: string[] = [];
      if (created) summary.push(`${created} created`);
      if (updated) summary.push(`${updated} updated`);
      if (present) summary.push(`${present} already in place`);
      if (errored.length) summary.push(`${errored.length} failed`);
      setAutoAddResult(`✓ Records on ${res.zone}: ${summary.join(', ')}`);

      // Trigger an immediate refresh so the status pills move.
      const refreshed = await domainsApi.get(domain.id);
      setDomain(refreshed);
    } catch (e) {
      const msg =
        e instanceof ApiError ? autoAddErrorFor(e.code) : (e as Error).message;
      setErr(msg);
    } finally {
      setAutoAddBusy(false);
    }
  }

  async function onConfirmDelete() {
    if (!domain) return;
    await domainsApi.delete(domain.id);
    navigate('/app/domains');
  }

  // If we're loading an existing domain (?id=...) — show a skeleton, not the
  // empty "Add your domain" form. Otherwise we flash the wrong UI for 1-2s
  // until the fetch resolves.
  if (existingId && !domain) {
    return (
      <DashboardLayout title="Loading domain">
        <div className="mx-auto max-w-2xl">
          <RouterLink
            to="/app/domains"
            className="text-sm text-[var(--color-muted-foreground)] hover:underline"
          >
            ← Back to domains
          </RouterLink>
          <div className="mt-4 h-9 w-64 animate-pulse rounded-md bg-[var(--color-muted)]" />
          <div className="mt-6 flex items-center gap-3">
            <div className="h-4 w-32 animate-pulse rounded bg-[var(--color-muted)]" />
            <div className="h-4 w-32 animate-pulse rounded bg-[var(--color-muted)]" />
          </div>
          <div className="mt-6 h-40 animate-pulse rounded-xl bg-[var(--color-muted)]" />
          {err && <p className="mt-2 text-sm text-[var(--color-destructive)]">{err}</p>}
        </div>
      </DashboardLayout>
    );
  }

  if (!domain) {
    return (
      <DashboardLayout title="Add domain">
        <div className="mx-auto max-w-lg">
          <RouterLink to="/app/domains" className="text-sm text-[var(--color-muted-foreground)] hover:underline">
            ← Back to domains
          </RouterLink>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">Add your domain</h1>
          <p className="mt-2 text-[var(--color-muted-foreground)]">
            Use a domain you already own. We&apos;ll show you the DNS records to add at
            your registrar — should be live in under 5 minutes.
          </p>

          <Surface tone="raised" className="mt-7 p-7">
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
                  Hostname
                </span>
                <input
                  type="text"
                  value={hostname}
                  onChange={(e) => setHostname(e.target.value.toLowerCase())}
                  placeholder="links.yourbrand.com"
                  required
                  pattern="[a-z0-9.\-]+"
                  autoFocus
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-3 font-mono outline-none transition focus:border-[var(--color-ring)]"
                />
                <span className="text-xs text-[var(--color-on-surface-variant)]">
                  Use a subdomain (<code className="font-mono">links.yourbrand.com</code>) or
                  the apex (<code className="font-mono">yourbrand.com</code>). Setup is
                  identical: one CNAME plus a TXT to verify ownership. A few legacy
                  registrars don&apos;t allow CNAMEs at the apex — if yours
                  doesn&apos;t, use a subdomain instead.
                </span>
              </label>

              <p className="text-xs text-[var(--color-on-surface-variant)]">
                Tip: one domain holds unlimited links by short-code — e.g.{' '}
                <code className="font-mono">{hostname || 'yourbrand.com'}/promo</code>,{' '}
                <code className="font-mono">{hostname || 'yourbrand.com'}/xyz</code>. If you
                want a different host (like <code className="font-mono">bio.{hostname || 'yourbrand.com'}</code>),
                add it here as its own domain — each gets its own SSL cert in a
                few minutes.
              </p>

              {err && <p className="text-sm text-[var(--color-destructive)]">{err}</p>}

              <Button type="submit" disabled={busy} size="lg" fullWidth>
                {busy ? 'Adding…' : 'Add domain →'}
              </Button>
            </form>
          </Surface>
        </div>
      </DashboardLayout>
    );
  }

  const apexReady = domain.verificationStatus === 'active' && domain.sslStatus === 'active';
  const wildcardReady = domain.wildcardEnabled
    ? domain.wildcardVerificationStatus === 'active' && domain.wildcardSslStatus === 'active'
    : true;
  const ready = apexReady && wildcardReady;

  return (
    <DashboardLayout title={domain.hostname}>
      <div className="mx-auto max-w-2xl">
        <RouterLink to="/app/domains" className="text-sm text-[var(--color-muted-foreground)] hover:underline">
          ← Back to domains
        </RouterLink>
        <h1 className="mt-4 font-mono text-2xl font-semibold tracking-tight">
          {domain.hostname}
        </h1>

        {ready ? (
          <Surface
            tone="raised"
            className="mt-6 p-7"
            style={{
              borderColor: 'rgba(125, 255, 162, 0.4)',
              boxShadow: '0 0 32px -8px rgba(125, 255, 162, 0.3)',
            }}
          >
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full text-2xl"
              style={{
                background: 'rgba(125, 255, 162, 0.15)',
                color: '#7dffa2',
              }}
            >
              ✓
            </div>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight">
              Live and ready
            </h2>
            <p className="mt-2 text-sm text-[var(--color-on-surface-variant)]">
              SSL is active, DNS is verified. Create links and they&apos;ll work at{' '}
              <code className="font-mono">https://{domain.hostname}/...</code>
            </p>
            <ButtonLink to="/app/links/new" variant="primary" className="mt-5">
              Create a link on this domain →
            </ButtonLink>
          </Surface>
        ) : (
          <>
            <div className="mt-6 flex flex-wrap items-center gap-2 text-sm text-[var(--color-on-surface-variant)]">
              <UIStatusPill tone={pillTone(domain.verificationStatus)} label={`DNS: ${domain.verificationStatus}`} />
              <UIStatusPill tone={pillTone(domain.sslStatus)} label={`SSL: ${domain.sslStatus}`} />
              {domain.wildcardEnabled && (
                <>
                  <UIStatusPill
                    tone={pillTone(domain.wildcardVerificationStatus || 'pending')}
                    label={`* DNS: ${domain.wildcardVerificationStatus || 'pending'}`}
                  />
                  <UIStatusPill
                    tone={pillTone(domain.wildcardSslStatus || 'pending')}
                    label={`* SSL: ${domain.wildcardSslStatus || 'pending'}`}
                  />
                </>
              )}
              <span className="text-xs">(checking every 5s)</span>
            </div>

            {/*
              Show the Cloudflare connect card only when relevant:
              - DNS check still loading (don't flash a hide/show) OR
              - The domain is on Cloudflare DNS OR
              - The user already connected (so they can manage / disconnect)
              For non-CF domains, no point cluttering the UI with the CTA.
            */}
            {(nsCheck === null || nsCheck.isCloudflare || cfIntegration?.connected) && (
              <div className="mt-6">
                <CloudflareConnect
                  onChanged={setCfIntegration}
                  detected={nsCheck?.isCloudflare === true}
                />
              </div>
            )}

            {domain.dns && (
              <Surface tone="raised" className="mt-6 p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
                    Add these records at your registrar
                  </h2>
                  {cfIntegration?.connected && (
                    <button
                      onClick={onAutoAddDns}
                      disabled={autoAddBusy}
                      className="rounded-full bg-[#f48120] px-4 py-1.5 text-xs font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
                      title="Use your connected Cloudflare token to write all records directly into your zone."
                    >
                      {autoAddBusy ? 'Adding…' : '⚡ Add to Cloudflare automatically'}
                    </button>
                  )}
                </div>

                {autoAddResult && (
                  <p className="mt-3 text-sm text-[var(--color-secondary)]">{autoAddResult}</p>
                )}

                {domain.dns.apex && (
                  <DnsGroup
                    title={
                      domain.wildcardEnabled
                        ? `For ${domain.hostname}`
                        : null
                    }
                    dns={domain.dns.apex}
                  />
                )}
                {domain.dns.wildcard && (
                  <DnsGroup
                    title={`For *.${domain.hostname}`}
                    dns={domain.dns.wildcard}
                  />
                )}

                <p className="mt-4 text-xs text-[var(--color-on-surface-variant)]">
                  Common registrars: Namecheap / GoDaddy / Squarespace / Google
                  Domains. After adding records, this page auto-detects the
                  change within a minute or two. SSL provisioning typically
                  takes another 5–15 min.
                  {domain.wildcardEnabled && (
                    <> Wildcard SSL ({domain.wildcardSslStatus ?? 'pending'}) sometimes
                    takes a bit longer than the apex — both must be active
                    before subdomain links resolve.</>
                  )}
                </p>
              </Surface>
            )}
          </>
        )}

        <button
          onClick={() => setRemoveOpen(true)}
          className="mt-8 text-sm text-[var(--color-destructive)] hover:underline"
        >
          Remove this domain
        </button>

        {err && <p className="mt-2 text-sm text-[var(--color-destructive)]">{err}</p>}
      </div>

      <ConfirmDialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        onConfirm={onConfirmDelete}
        title={`Remove ${domain.hostname}?`}
        description={
          <>
            {domain.verificationStatus === 'active' && domain.sslStatus === 'active' ? (
              <p>
                Any links using <code className="font-mono">{domain.hostname}</code> will
                stop resolving immediately. Followers who tap them will get a 404 until you
                re-add the domain.
              </p>
            ) : (
              <p>
                This domain isn&apos;t live yet (still pending verification), so no
                followers are tapping links on it. Removing now is safe.
              </p>
            )}
            <p className="mt-2 text-[var(--color-muted-foreground)]">
              You can re-add it later — any DNS records you&apos;ve set at your registrar
              will be reusable.
            </p>
          </>
        }
        requireText={domain.hostname}
        confirmLabel="Remove domain"
        destructive
      />
    </DashboardLayout>
  );
}

function DnsRow({ type, name, value }: { type: string; name: string; value: string }) {
  return (
    <div className="mt-4 grid grid-cols-[80px_1fr] gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] p-4 font-mono text-sm">
      <div className="text-[var(--color-on-surface-variant)]">Type</div>
      <div>{type}</div>
      <div className="text-[var(--color-on-surface-variant)]">Name</div>
      <CopyableValue value={name} />
      <div className="text-[var(--color-on-surface-variant)]">Value</div>
      <CopyableValue value={value} />
    </div>
  );
}

/**
 * A pair of DNS records (CNAME + optional TXT) for a single hostname.
 * Used twice on the page when wildcard is enabled — once for the apex
 * (`brand.com`) and once for the wildcard sibling (`*.brand.com`) — so
 * the customer can copy/paste each set into their registrar separately.
 */
function DnsGroup({
  title,
  dns,
}: {
  title: string | null;
  dns: { cname: { name: string; value: string }; ownership?: { type: 'txt' | 'http'; name: string; value: string } };
}) {
  return (
    <div className="mt-5">
      {title && (
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-primary)]">
          {title}
        </h3>
      )}
      <DnsRow type="CNAME" name={dns.cname.name} value={dns.cname.value} />
      {dns.ownership && dns.ownership.type === 'txt' && (
        <DnsRow type="TXT" name={dns.ownership.name} value={dns.ownership.value} />
      )}
    </div>
  );
}

function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="flex items-center gap-2 text-left text-[var(--color-foreground)] transition hover:text-[var(--color-primary)]"
      title="Copy"
    >
      <span className="break-all">{value}</span>
      <span className="text-xs text-[var(--color-muted-foreground)]">{copied ? '✓' : '⧉'}</span>
    </button>
  );
}

function pillTone(status: string): 'success' | 'pending' | 'error' | 'neutral' {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending':
      return 'pending';
    case 'failed':
      return 'error';
    default:
      return 'neutral';
  }
}

function autoAddErrorFor(code: string): string {
  switch (code) {
    case 'integration_not_connected':
      return 'Connect Cloudflare first (the panel above).';
    case 'no_matching_zone':
      return 'Your Cloudflare token doesn\'t have access to a zone that owns this hostname. Add the parent zone to the token scope.';
    case 'decrypt_failed':
      return 'Stored token couldn\'t be decrypted. Disconnect and reconnect.';
    case 'domain_not_initialized':
      return 'This domain hasn\'t been registered with our SaaS layer yet — try again in a moment.';
    default:
      return code;
  }
}

function errorMessageFor(code: string, detail?: string): string {
  switch (code) {
    case 'hostname_invalid':
      return "That doesn't look like a valid hostname. Use the form: subdomain.yourbrand.com";
    case 'hostname_reserved':
      return 'That hostname belongs to Poof itself — pick your own.';
    case 'hostname_taken':
      return 'Someone has already claimed that hostname on Poof.';
    case 'plan_limit_reached':
      return 'You\'ve reached your plan\'s custom domain limit. Upgrade to add more.';
    case 'cf_api_failed':
      return detail
        ? `Cloudflare rejected the request: ${detail}`
        : 'Cloudflare rejected the request. Double-check the hostname format.';
    case 'db_insert_failed':
      return detail
        ? `Couldn't save the domain: ${detail}`
        : "Couldn't save the domain. Try again in a moment.";
    default:
      return detail ? `${code}: ${detail}` : code;
  }
}
